import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { EAuth, LogoutReason } from '@/config/enums';
import { arrayPayloadInitial } from '@/contexts/AuthContext';
import { saveLogoutNotice, signOut } from '@/helpers/auth';
import { clearDataBase } from '@/helpers/database-helpers';
import { arrayAttendance } from '@/hooks/useAttendence';
import {
  ESocketErrorCode,
  ISocketConnectError,
  socketEvents,
} from '@/models/Socket';
import { URLCliente } from '@/services/api';
import { parseCookies } from 'nookies';
import { Socket, io } from 'socket.io-client';

type SocketEventHandler = (...args: any[]) => void;

type SocketContextData = {
  socket: Socket | null;
  isConnected: boolean;
  connect: () => Promise<Socket | null>;
  emit: (event: string, ...args: any[]) => void;
  reconnect: () => void;
  disconnect: () => void;
};

const SocketContext = createContext<SocketContextData | null>(null);

function redirectToLogin() {
  window.location.href = '/auth/login';
}

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const pendingConnectRef = useRef<Promise<Socket | null> | null>(null);

  const cleanup = useCallback(async () => {
    try {
      await clearDataBase();
      arrayAttendance.length = 0;
    } catch (error) {
      console.error('Erro ao limpar o banco de dados:', error);
    }
  }, []);

  const disconnect = useCallback(async () => {
    pendingConnectRef.current = null;

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
  }, []);

  const connect = useCallback((): Promise<Socket | null> => {
    if (pendingConnectRef.current) return pendingConnectRef.current;
    if (socketRef.current) return Promise.resolve(socketRef.current);

    const cookies = parseCookies();
    const token = cookies[EAuth.TOKEN_KEY];

    if (!token) return Promise.resolve(null);

    const promise = new Promise<Socket | null>((resolve) => {
      const newSocket = io(URLCliente.URLHOSTSOCKET, {
        auth: { token },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
      });

      socketRef.current = newSocket;

      const finish = (value: Socket | null) => {
        pendingConnectRef.current = null;
        resolve(value);
      };

      newSocket.on('connect', () => {
        setIsConnected(true);
        socketRef.current = newSocket;
        setSocket(newSocket);
        finish(newSocket);
      });

      newSocket.on('disconnect', async (reason) => {
        setIsConnected(false);

        if (
          reason === 'io server disconnect' ||
          reason === 'io client disconnect'
        ) {
          socketRef.current = null;
          setSocket(null);
          await cleanup();
        }
      });

      newSocket.on('connect_error', async (error: ISocketConnectError) => {
        console.error('Erro na conexao com o servidor Socket.IO:', error);
        setIsConnected(false);

        const code = error.data?.code;

        if (
          code === ESocketErrorCode.SESSION_REPLACED ||
          code === ESocketErrorCode.SESSION_INVALIDATED
        ) {
          newSocket.disconnect();
          socketRef.current = null;
          setSocket(null);
          finish(null);

          if (code === ESocketErrorCode.SESSION_REPLACED)
            saveLogoutNotice(LogoutReason.SESSION_REPLACED);

          await signOut(redirectToLogin).catch(redirectToLogin);
          return;
        }

        if (
          code === ESocketErrorCode.AUTH_TOKEN_INVALID ||
          code === ESocketErrorCode.AUTH_TOKEN_MISSING
        ) {
          const cookies = parseCookies();
          const freshToken = cookies[EAuth.TOKEN_KEY];

          if (freshToken && code === ESocketErrorCode.AUTH_TOKEN_INVALID) {
            newSocket.auth = { token: freshToken };
            return;
          }

          newSocket.disconnect();
          socketRef.current = null;
          setSocket(null);
          finish(null);
          window.location.href = '/auth/login';
          return;
        }
      });

      newSocket.io.on('reconnect_attempt', () => {
        const cookies = parseCookies();
        const freshToken = cookies[EAuth.TOKEN_KEY];
        if (freshToken) {
          newSocket.auth = { token: freshToken };
        }
      });

      newSocket.io.on('reconnect', () => {
        setIsConnected(true);

        if (
          arrayPayloadInitial &&
          Object.keys(arrayPayloadInitial).length > 0
        ) {
          newSocket.emit(socketEvents.USER_LOGGED, {
            ...arrayPayloadInitial,
            onLogin: false,
          });
        }
      });
    });

    pendingConnectRef.current = promise;
    return promise;
  }, [cleanup]);

  const reconnect = useCallback(async () => {
    await disconnect();
    await cleanup();
    connect();
  }, [disconnect, cleanup, connect]);

  const emit = useCallback((event: string, ...args: any[]) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, ...args);
    } else {
      console.warn(`Socket desconectado. Evento "${event}" nao emitido.`);
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!socketRef.current || socketRef.current.connected) return;

      socketRef.current.connect();
    };

    const handleOnline = () => {
      if (!socketRef.current || socketRef.current.connected) return;

      socketRef.current.connect();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);

      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        connect,
        emit,
        reconnect,
        disconnect,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket(): SocketContextData {
  const ctx = useContext(SocketContext);

  if (!ctx) {
    throw new Error('useSocket deve ser usado dentro do SocketProvider');
  }

  return ctx;
}

export function useSocketEvent(event: string, handler: SocketEventHandler) {
  const { socket } = useSocket();
  const handlerRef = useRef(handler);

  handlerRef.current = handler;

  useEffect(() => {
    if (!socket) return;

    const listener = (...args: any[]) => handlerRef.current(...args);

    socket.on(event, listener);

    return () => {
      socket.off(event, listener);
    };
  }, [socket, event]);
}
